type WindowWithDataLayer = Window & {
  dataLayer: Record<string, unknown>[];
  _gtmInitialized?: boolean;
  _gtmStepsTracked?: Record<string, boolean>;
  _gtmTrackedEvents?: Record<string, boolean>;
};

declare const window: WindowWithDataLayer;

export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
const PREVIOUS_URL_STORAGE_KEY = "gtm_previous_url";

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  index?: number;
  item_list_id?: string;
  item_list_name?: string;
  price?: number;
}

interface VirtualPageViewData {
  pageUrl: string;
  country: string | string[];
  lang: string | string[];
  brand: string;
  type: string;
  division?: string;
}

function getSanitizedPageLocation(): string {
  if (typeof window === "undefined") {
    return "";
  }

  const url = new URL(window.location.href);

  url.searchParams.delete("email");

  return url.toString();
}

export const initializeGTM = () => {
  if (typeof window === "undefined" || window._gtmInitialized) {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  function gtag(...args: unknown[]) {
    window.dataLayer.push(args as unknown as Record<string, unknown>);
  }

  // Set default consent settings
  gtag("consent", "default", {
    ad_user_data: "denied",
    ad_personalization: "denied",
    ad_storage: "denied",
    analytics_storage: "denied",
    wait_for_update: 2000, // milliseconds to wait for update
  });

  // Enable ads data redaction by default [optional]
  gtag("set", "ads_data_redaction", true);

  window._gtmInitialized = true;
  // Override page_location to exclude sensitive URL parameters
  gtag("set", "page_location", getSanitizedPageLocation());
};

export const pageviewToDataLayer = (
  country: string | string[],
  lang: string | string[],
  brand: string,
  type: string,
  division?: string
) => {
  if (
    typeof window === "undefined" ||
    typeof window.dataLayer === "undefined"
  ) {
    return;
  }

  const page = {
    country: country ?? "",
    language: lang ?? "",
    brand: brand ?? "",
    type: type ?? "",
    ...(division ? { division } : {}),
  };

  pushEvent("dataLayer_ready", {
    page,
  });
};

export const virtualPageviewToDataLayer = (data: VirtualPageViewData) => {
  if (typeof window === "undefined") {
    return;
  }

  const previousUrl =
    window.sessionStorage.getItem(PREVIOUS_URL_STORAGE_KEY) ?? "";

  pushEvent("virtual_page_view", {
    pageUrl: data.pageUrl,
    pageTitle: typeof document !== "undefined" ? document.title : "",
    previousUrl,
    page: {
      country: data.country ?? "",
      language: data.lang ?? "",
      brand: data.brand ?? "",
      type: data.type ?? "",
      ...(data.division ? { division: data.division } : {}),
    },
  });

  window.sessionStorage.setItem(PREVIOUS_URL_STORAGE_KEY, data.pageUrl);
};

export const viewItemListToDataLayer = (
  items: EcommerceItem[],
  listName: string,
  listId?: string
) => {
  if (!items.length) {
    return;
  }

  pushEvent("view_item_list", {
    ecommerce: {
      item_list_name: listName,
      ...(listId ? { item_list_id: listId } : {}),
      items,
    },
  });
};

export const selectItemToDataLayer = (
  item: EcommerceItem,
  listName: string,
  listId?: string
) => {
  pushEvent("select_item", {
    ecommerce: {
      item_list_name: listName,
      ...(listId ? { item_list_id: listId } : {}),
      items: [item],
    },
  });
};

export const videoStartToDataLayer = (videoTitle: string, videoUrl: string) => {
  pushEvent("video_start", {
    video_title: videoTitle,
    video_url: videoUrl,
  });
};

export const videoProgressToDataLayer = (
  videoTitle: string,
  videoUrl: string,
  progressPercent: number,
  currentTime: number,
  duration: number
) => {
  pushEvent("video_progress", {
    video_title: videoTitle,
    video_url: videoUrl,
    video_percent: progressPercent,
    video_current_time: currentTime,
    video_duration: duration,
  });
};

export const videoCompleteToDataLayer = (
  videoTitle: string,
  videoUrl: string,
  duration: number
) => {
  pushEvent("video_complete", {
    video_title: videoTitle,
    video_url: videoUrl,
    video_duration: duration,
  });
};

export const registrationAccessToDataLayer = (
  country: string | string[],
  lang: string | string[],
  source: string
) => {
  if (typeof window.dataLayer !== "undefined") {
    pushEvent("bike_registration_link", {
      page: {
        country: country ?? "",
        language: lang ?? "",
        source,
      },
    });
  }
};

export const registrationStartToDataLayer = (
  country: string | string[],
  lang: string | string[],
  brand: string,
  division: string
) => {
  if (typeof window.dataLayer !== "undefined") {
    pushEvent("product_registration_start", {
      page: {
        country: country ?? "",
        language: lang ?? "",
        brand: brand ?? "",
        division: division ?? "",
      },
      user: {
        id: "",
        login_status: "",
        login_method: "",
      },
    });
  }
};

export const registrationSucceededToDataLayer = (
  country: string | string[],
  lang: string | string[],
  brand: string,
  division: string
) => {
  pushEvent(
    "product_registration_success",
    {
      page: {
        country: country ?? "",
        language: lang ?? "",
        brand: brand ?? "",
        division: division ?? "",
      },
      user: {
        id: "",
        login_status: "",
        login_method: "",
      },
    },
    { once: true }
  );
};

export const pushEvent = (
  event: string,
  data: Record<string, unknown>,
  options?: { once?: boolean }
) => {
  if (typeof window.dataLayer === "undefined") {
    return;
  }

  window._gtmTrackedEvents = window._gtmTrackedEvents || {};

  if (options?.once) {
    if (window._gtmTrackedEvents[event]) {
      return;
    }
    window._gtmTrackedEvents[event] = true;
  }

  window.dataLayer.push({ event, ...data });

  if (process.env.NODE_ENV === "development") {
    // Development logging can be added here if needed
  }
};

export const registrationStep2ToDataLayer = (
  country: string | string[],
  lang: string | string[],
  brand: string,
  division: string
) => {
  if (typeof window.dataLayer !== "undefined") {
    window._gtmStepsTracked = window._gtmStepsTracked || {};

    if (window._gtmStepsTracked.step2) {
      return;
    }

    pushEvent("product_registration_step2", {
      page: {
        country: country ?? "",
        language: lang ?? "",
        brand: brand ?? "",
        division: division ?? "",
      },
    });

    window._gtmStepsTracked.step2 = true;
  }
};

export const registrationStep3ToDataLayer = (
  country: string | string[],
  lang: string | string[],
  brand: string,
  division: string
) => {
  if (typeof window.dataLayer !== "undefined") {
    window._gtmStepsTracked = window._gtmStepsTracked || {};

    if (window._gtmStepsTracked.step3) {
      return;
    }

    pushEvent("product_registration_step3", {
      page: {
        country: country ?? "",
        language: lang ?? "",
        brand: brand ?? "",
        division: division ?? "",
      },
    });

    window._gtmStepsTracked.step3 = true;
  }
};
